// Production Activities: one shared record per activity (Backend: the
// "Production Activities" tab), shown by the Standard page and every
// Multi-Event packet that includes one of its projects. These helpers turn a
// record into the Run of Show event form and back, and name what removing it
// does, so both builders behave the same.
import { ROS_ACTIVITY_OPTIONS, resolveActivity, newId, clockMinutes } from './rosActivity';
import { savedLocationRef } from './locations';

/** A new activity's form. In a Multi-Event packet it starts on all of the packet's projects. */
export function newActivityForm(memberIds = []) {
  return {
    id: newId(),
    isActivity: true,
    scope: 'all',
    dateISO: '',
    time: '',
    activity: '',
    customActivity: '',
    projectIds: memberIds.slice(),
    locationRef: '',
    timeZone: '',
    newLocationName: '',
    newLocationAddress: '',
    hardTime: '',
    notes: '',
    baseUpdatedAt: '',
    outsideCount: 0,
    createdAt: ''
  };
}

/** The form for editing a saved activity (a record from the view's `activities`). */
export function activityFormFrom(rec, warehouseRef, memberIds = []) {
  const predefined = ROS_ACTIVITY_OPTIONS.indexOf(rec.activity) !== -1;
  const loc = rec.location || {};
  let locationRef = '';
  let newLocationName = '';
  let newLocationAddress = '';
  if (loc.type === 'warehouse') locationRef = warehouseRef || 'warehouse';
  else if (/^eps:/.test(rec.locationRef || '')) locationRef = rec.locationRef;
  else if (loc.name) {
    // A location created earlier reopens as itself (its name, address and time zone come with it).
    locationRef = loc.type === 'custom' ? savedLocationRef(loc) : 'new';
    newLocationName = loc.name;
    newLocationAddress = loc.address || '';
  }
  const ids = rec.projectIds || [];
  const scope = rec.wholeOperation ? 'whole'
    : (memberIds.length > 1 && memberIds.every((id) => ids.indexOf(id) !== -1) ? 'all' : 'chosen');
  return {
    ...newActivityForm(),
    id: rec.id,
    scope,
    dateISO: rec.dateISO || '',
    time: rec.time || '',
    activity: predefined ? rec.activity : (rec.activity ? 'Other' : ''),
    customActivity: predefined ? '' : (rec.activity || ''),
    projectIds: ids.slice(),
    locationRef,
    newLocationName,
    newLocationAddress,
    timeZone: rec.timeZone || '',
    hardTime: rec.hardTime || '',
    notes: rec.notes || '',
    baseUpdatedAt: rec.updatedAt || '',
    outsideCount: rec.outsideCount || 0,
    createdAt: rec.createdAt || ''
  };
}

/**
 * What saveProductionActivity receives. "All projects" saves the packet's
 * explicit project list; only "Whole operation" makes a packet-owned activity.
 */
export function activityPayload(form, { memberIds = [], warehouseRef = 'warehouse' } = {}) {
  const ref = form.locationRef || '';
  let location = { type: '' };
  if (ref === warehouseRef) location = { type: 'warehouse' };
  else if (/^eps:/.test(ref)) location = { type: 'venue', projectId: ref.slice(4) };
  else if (ref === 'new' || /^saved:/.test(ref)) {
    location = { type: 'custom', name: String(form.newLocationName || '').trim(), address: String(form.newLocationAddress || '').trim() };
  }
  const scope = form.scope || 'chosen';
  return {
    id: form.id,
    dateISO: form.dateISO,
    time: String(form.time || '').trim(),
    timeZone: form.timeZone || '',
    activity: resolveActivity(form.activity, form.customActivity),
    projectIds: scope === 'all' ? memberIds.slice() : scope === 'whole' ? [] : (form.projectIds || []).slice(),
    wholeOperation: scope === 'whole',
    location,
    hardTime: String(form.hardTime || '').trim(),
    notes: String(form.notes || '').trim()
  };
}

/**
 * The words for removing an activity from a view, so a coordinator always knows
 * whether it leaves this project / these projects or is deleted outright.
 * context: 'standard' | 'packet'.
 */
export function removalLabel(rec, context) {
  if (!rec) return 'Delete activity';
  if (rec.wholeOperation) return 'Delete activity';
  if ((rec.outsideCount || 0) > 0) return context === 'standard' ? 'Remove from this project' : 'Remove from these projects';
  return 'Delete activity';
}

/** The confirmation for that removal. */
export function removalConfirmText(rec, context) {
  const name = (rec && rec.activity) || 'this activity';
  const others = (rec && rec.outsideCount) || 0;
  if (removalLabel(rec, context) === 'Delete activity') {
    return `Delete “${name}”? It is removed from every Run of Show that shows it.`;
  }
  const where = context === 'standard' ? 'this project' : 'this packet’s projects';
  return `Remove “${name}” from ${where}? It stays on ${others} other project${others === 1 ? '' : 's'}.`;
}

/** A shared activity's note in the Run of Show source line. */
export function sharedNote(rec) {
  const n = (rec && rec.outsideCount) || 0;
  return n ? `also on ${n} other project${n === 1 ? '' : 's'}` : '';
}

/**
 * The Run of Show row shown while a save is in flight (marked pending), until
 * the saved, server-sorted Run of Show replaces it.
 * places: { warehouse, members: [{ projectId, venueName, venueAddress }] }
 */
export function pendingActivityRow(form, payload, { warehouse, members = [] } = {}) {
  const ref = form.locationRef || '';
  let venue = '';
  let venueAddress = '';
  if (warehouse && ref === warehouse.ref) { venue = warehouse.name; venueAddress = warehouse.address; }
  else if (/^eps:/.test(ref)) {
    const m = members.find((x) => `eps:${x.projectId}` === ref) || {};
    venue = m.venueName || ''; venueAddress = m.venueAddress || '';
  } else if (payload.location && payload.location.type === 'custom') { venue = payload.location.name; venueAddress = payload.location.address; }
  const key = `act:${payload.id}`;
  return {
    id: key, key, keys: [key], source: 'manual', store: true, activityId: payload.id, pending: true,
    activity: payload.activity, dateISO: payload.dateISO,
    timeMinutes: clockMinutes(payload.time), timeDisplay: payload.time,
    timeZone: payload.timeZone, hardTime: payload.hardTime, notes: payload.notes,
    venue, venueAddress, projectIds: payload.projectIds, hideable: false,
    createdAt: form.createdAt || new Date().toISOString()
  };
}
